import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    first_name: '', last_name: '', work_email: '', org_name: '',
    phone: '', industry: '', org_size: '', designation: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.work_email || !form.org_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/access-request`, form);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="request-success">
          <div className="bg-green-50 border border-green-200 rounded-sm p-8 mb-6">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-[800] text-[#003366] mb-2">Request Submitted</h2>
            <p className="text-slate-600 leading-relaxed">
              Thank you for your interest in the CX0 DPDP Readiness Assessment.
              Our team will review your request and get back to you shortly at <span className="font-medium">{form.work_email}</span>.
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" className="rounded-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CX0" className="h-10" />
            <span className="text-xl font-[800] text-[#003366] tracking-tight">DPDP Assessment</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Left — Info */}
          <div className="md:col-span-2">
            <h1 className="text-3xl font-[800] text-[#003366] leading-tight mb-4">
              Request Access
            </h1>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Fill in your details and our team will set up your DPDP Readiness Assessment account. You'll receive your login credentials via email.
            </p>

            <div className="space-y-4">
              <h3 className="text-sm font-[700] uppercase tracking-[0.15em] text-[#008080]">What You Get</h3>
              {[
                "83-question structured assessment",
                "15 DPDP compliance pillars",
                "SDF risk assessment",
                "Evidence planner for audit readiness",
                "12-18 month compliance roadmap",
                "Detailed DOCX compliance report"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#008080] shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="md:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-sm p-8 shadow-sm" data-testid="request-access-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>First Name <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="First name"
                    value={form.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    className="mt-1 rounded-sm"
                    data-testid="input-first-name"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="Last name"
                    value={form.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    className="mt-1 rounded-sm"
                    data-testid="input-last-name"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <Label>Work Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={form.work_email}
                  onChange={(e) => handleChange('work_email', e.target.value)}
                  className="mt-1 rounded-sm"
                  data-testid="input-work-email"
                  required
                />
              </div>

              <div className="mb-4">
                <Label>Organisation Name <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Your company name"
                  value={form.org_name}
                  onChange={(e) => handleChange('org_name', e.target.value)}
                  className="mt-1 rounded-sm"
                  data-testid="input-org-name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="mt-1 rounded-sm"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    placeholder="e.g. DPO, CISO, CTO"
                    value={form.designation}
                    onChange={(e) => handleChange('designation', e.target.value)}
                    className="mt-1 rounded-sm"
                    data-testid="input-designation"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Industry</Label>
                  <Select value={form.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="select-industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {["BFSI", "Healthcare", "IT / SaaS", "Manufacturing", "Retail / E-commerce", "Education", "Pharmaceutical", "Telecom", "Government / PSU", "Other"].map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Organisation Size</Label>
                  <Select value={form.org_size} onValueChange={(v) => handleChange('org_size', v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="select-org-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {["1-50 employees", "51-200 employees", "201-500 employees", "501-1000 employees", "1001-5000 employees", "5000+ employees"].map(sz => (
                        <SelectItem key={sz} value={sz}>{sz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-6">
                <Label>Message <span className="text-slate-400">(optional)</span></Label>
                <textarea
                  placeholder="Any specific requirements or questions?"
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="mt-1 w-full rounded-sm border border-slate-200 px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:border-transparent"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm text-base"
                data-testid="submit-request-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Send className="h-5 w-5 mr-2" />
                )}
                Submit Access Request
              </Button>

              <p className="text-xs text-slate-400 text-center mt-4">
                By submitting, you agree to be contacted regarding the DPDP Readiness Assessment.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
